import React from 'react';
import { TrendingUp, Target, Lightbulb, TrendingDown } from 'lucide-react';
import { processClickableText } from '../../utils/analysis';

import ReactMarkdown from 'react-markdown';

const InsightsPanel = ({
    insights,
    loadingInsights,
    generateInsights,
    analysis,
    setActiveTab
}) => {
    const renderClickableText = (text) => {
        const parts = processClickableText(text);
        return parts.map((part, i) => {
            if (part.isLink) {
                return (
                    <button
                        key={i}
                        onClick={() => setActiveTab(part.tab)}
                        className="text-blue-600 hover:text-blue-800 underline font-semibold cursor-pointer"
                        title={`Vai alla tab ${part.tab}`}
                    >
                        {part.text}
                    </button>
                );
            }
            // Use ReactMarkdown for non-link parts to support bolding
            return (
                <span key={i}>
                    <ReactMarkdown
                        components={{
                            p: ({ node, ...props }) => <span {...props} />
                        }}
                    >
                        {part.text}
                    </ReactMarkdown>
                </span>
            );
        });
    };

    // Shared style for markdown content
    const markdownStyle = "text-sm text-gray-700 leading-relaxed [&>p]:mb-2 [&>ul]:list-disc [&>ul]:pl-5 [&>ol]:list-decimal [&>ol]:pl-5 [&>strong]:font-bold [&>h3]:text-lg [&>h3]:font-bold [&>h3]:mt-3 [&>h3]:mb-1 [&>blockquote]:border-l-4 [&>blockquote]:border-gray-300 [&>blockquote]:pl-4 [&>blockquote]:italic [&>cite]:text-xs [&>cite]:text-gray-500";

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <Target className="text-blue-500" />
                    Insights AI
                </h2>
                <button
                    onClick={() => generateInsights(analysis)}
                    disabled={loadingInsights}
                    className={`px-6 py-2 rounded-lg font-bold text-white transition-all ${loadingInsights ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-md active:scale-95'
                        }`}
                >
                    {loadingInsights ? 'Generazione in corso...' : (insights ? 'Rigenere Insights' : 'Genera Insights AI')}
                </button>
            </div>

            {insights && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-4 bg-blue-50 rounded-lg">
                            <strong className="block mb-2 text-blue-900">SERP Features & Visibility</strong>
                            <div className={markdownStyle}>
                                <ReactMarkdown>{insights.serpFeatures}</ReactMarkdown>
                            </div>
                        </div>
                        <div className="p-4 bg-green-50 rounded-lg">
                            <strong className="block mb-2 text-green-900">Competitiveness</strong>
                            <div className={markdownStyle}>
                                <ReactMarkdown>{insights.competitiveness}</ReactMarkdown>
                            </div>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg">
                            <strong className="block mb-2 text-purple-900">Search Intent</strong>
                            <div className={markdownStyle}>
                                <ReactMarkdown>{insights.searchIntent}</ReactMarkdown>
                            </div>
                        </div>
                        <div className="p-4 bg-orange-50 rounded-lg">
                            <strong className="block mb-2 text-orange-900">Content Strategy</strong>
                            <div className={markdownStyle}>
                                <ReactMarkdown>{insights.contentStrategy}</ReactMarkdown>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border-l-4 border-orange-500">
                            <strong className="block mb-3 text-orange-900 text-lg flex items-center gap-2">
                                <TrendingUp size={20} />
                                Action Items
                            </strong>
                            <ul className="space-y-2">
                                {insights.actionItems.map((action, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm">
                                        <span className="text-orange-600 font-bold mt-0.5">✓</span>
                                        <span className="text-gray-700 block w-full">{renderClickableText(action)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border-l-4 border-green-500">
                            <strong className="block mb-3 text-green-900 text-lg flex items-center gap-2">
                                <Lightbulb size={20} />
                                Quick Wins
                            </strong>
                            <ul className="space-y-2">
                                {insights.quickWins.map((win, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm">
                                        <span className="text-green-600 font-bold mt-0.5">💡</span>
                                        <span className="text-gray-700 block w-full">{renderClickableText(win)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {!insights && !loadingInsights && (
                <div className="text-center py-8 text-gray-500">
                    <p>Clicca su "Genera Insights AI" per ottenere un'analisi intelligente con action items e quick wins</p>
                </div>
            )}
        </div>
    );
};

export default InsightsPanel;
